import React from "react";
import { StyleSheet, View, TouchableOpacity } from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";
import ChevronRight from "@assets/svg/ChevronRight";
import ButtonError from "@assets/svg/DbManagement/Error";
import t from "@constants/localization";

interface OverviewCardProps {
  title: string;
  quantity: number;
  amount: number;
  amountLabel: string;
  onPress?: () => void;
  damaged_quantity_strip?: string;
  damaged_quantity?: number;
  isNavigationBlocked?: boolean;
}

const OverviewCard: React.FC<OverviewCardProps> = ({
  title,
  quantity,
  amount,
  amountLabel,
  onPress,
  damaged_quantity = 0,
  damaged_quantity_strip,
  isNavigationBlocked = false,
}) => {
  const PATH = "screens/MyDeliveries/Overview/components/OverviewCard";
  return (
    <TouchableOpacity
      style={styles.container}
      onPress={onPress}
      activeOpacity={0.8}
    >
      <View style={styles.header}>
        <Text variant="subtitle_16" style={styles.title}>
          {title}
        </Text>
        {!isNavigationBlocked ? <View style={styles.chevronContainer}>
          <ChevronRight
            style={styles.chevronIcon}
            pathFill={color.lightMediumBlack}
          />
        </View> : null}
      </View>

      <View style={styles.content}>
        <View style={styles.quantitySection}>
          <Text variant="caption_2" style={styles.label}>
            {t(`${PATH}/quantity`)}
          </Text>
          <View style={styles.quantityRow}>
            <Text variant="title_16" style={styles.quantity}>
              {quantity}
            </Text>
            {damaged_quantity ? (
              <View style={[styles.damagedContainer]}>
                <Text variant="caption_4" style={styles.damaged}>
                  {t(`${PATH}/damage`, { qty: damaged_quantity })}
                </Text>
              </View>
            ) : null}
          </View>
        </View>
        {amount ? (
          <View style={styles.amountSection}>
            <Text variant="caption_2" style={styles.label}>
              {amountLabel}
            </Text>
            <Text variant="title_16">{amount}</Text>
          </View>
        ) : null}
      </View>

      {damaged_quantity_strip ? (
        <View style={styles.warningContainer}>
          <ButtonError />
          <Text variant="caption_2" style={styles.warningText}>
            {damaged_quantity_strip}
          </Text>
        </View>
      ) : null}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: color.white,
    borderRadius: 16,
    padding: 12,
    marginVertical: 6,
    borderWidth: 1,
    borderColor: color.primaryBg,
    marginHorizontal: 16,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  chevronContainer: {
    backgroundColor: color.lightGrayWhite,
    padding: 3,
    borderRadius: 4,
  },
  chevronIcon: {
    height: 16,
    width: 16,
  },
  title: {
    color: color.black,
  },
  content: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    backgroundColor: color.primaryBg,
    borderRadius: 8,
    padding: 12,
  },
  quantitySection: {
    flex: 1,
  },
  amountSection: {
    flex: 1.5,
    marginLeft: 12,
    paddingLeft: 16,
    borderLeftWidth: 1,
    borderLeftColor: color.lighterBlack,
  },
  label: {
    color: color.mediumBlack,
  },
  quantityRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  quantity: {
    marginRight: 8,
  },
  damagedContainer: {
    backgroundColor: "rgba(255, 77, 79, 0.1)",
    borderRadius: 24,
    paddingHorizontal: 10,
    paddingVertical: 2,
    marginRight: 4,
  },
  damaged: {
    color: color.plain_red,
    lineHeight: 18,
  },
  warningContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 8,
    justifyContent: "center",
  },
  warningText: {
    flex: 1,
    color: color.mediumBlack,
    marginLeft: 4,
    top: 2,
  },
});

export default OverviewCard;
